import { Fragment, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, ChevronRight, Download, Printer, Search } from 'lucide-react'
import { getSubledger } from '../../api/accounting'
import { getApAging, getArAging } from '../../api/dashboard'
import AgingTable from './AgingTable'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { HelpTip } from '../../components/HelpTip'
import { StatusBadge } from '../../components/PageParts'
import { fmtDay } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { money } from '../sales/salesShared'
import { PeriodPicker, ReportsTabs, StatementHead, acct, downloadCsv, glHref, periodLabel, useCompanyName, usePeriodParams } from './reportsShared'
import { printPage, useAutoPrint } from '../../utils/print'

const KINDS = {
  ap: {
    title: 'Payables subledger', full: 'Accounts payable subledger', k: 'apSubledger', meta: 'What you owe each vendor, from the payables account, and how long it has been owed.',
    party: 'Vendor', id: 'vendor_id', name: 'vendor_name', up: 'cr', down: 'dr', upLabel: 'Billed', downLabel: 'Paid and credited',
    link: (id) => `/accounting/ap/vendor/${id}`, file: 'ap_subledger',
    aging: { fetch: getApAging, rows: 'vendors', title: 'Accounts payable aging', what: 'bills', one: 'bill', empty: 'No open bills.', link: (id) => `/accounting/ap/vendor/${id}` },
  },
  ar: {
    title: 'Receivables subledger', full: 'Accounts receivable subledger', k: 'arSubledger', meta: 'What each customer owes you, from the receivables account, and how long it has been owed.',
    party: 'Customer', id: 'customer_id', name: 'customer_name', up: 'dr', down: 'cr', upLabel: 'Charged', downLabel: 'Paid and credited',
    link: null, file: 'ar_subledger',
    aging: { fetch: getArAging, rows: 'customers', title: 'Accounts receivable aging', what: 'invoices', one: 'invoice', empty: 'No open invoices.', link: (id) => `/master/customers/${id}` },
  },
}

const amount = (v) => (Math.abs(Number(v) || 0) < 0.005 ? '' : money(v))

// A subledger: the control account (payables or receivables) split by
// vendor or customer, with each one's opening balance, increases,
// decreases and closing balance. Its total is the control account's.
export default function Subledger({ kind }) {
  const cfg = KINDS[kind]
  const company = useCompanyName()
  const { set, period, start, end, setPeriod } = usePeriodParams('ytd')
  const [data, setData] = useState(null)
  useAutoPrint(!!data)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState({})
  const [hideSettled, setHideSettled] = useState(false)
  // The aging: the open bills or invoices behind the closing balances, by
  // how long past due they are on the last day of the period.
  const [aging, setAging] = useState(null)
  useEffect(() => {
    if (!end) return
    setAging(null)
    cfg.aging.fetch({ as_of: end, limit: 0 }).then(setAging).catch(() => setAging({ [cfg.aging.rows]: [], totals: {} }))
  }, [kind, end, cfg])

  useEffect(() => {
    if (!start || !end) return
    setError('')
    setData(null)
    setOpen({})
    getSubledger(kind, { start, end })
      .then(setData)
      .catch((e) => setError(e.message || `Could not load ${cfg.title.toLowerCase()}.`))
  }, [kind, start, end, cfg.title])

  const parties = useMemo(() => {
    const term = search.trim().toLowerCase()
    return (data?.parties || [])
      .map((p) => ({ ...p, key: p[cfg.id] || 0, label: p[cfg.id] ? (p[cfg.name] || `${cfg.party} #${p[cfg.id]}`) : `Not linked to a ${cfg.party.toLowerCase()}` }))
      .filter((p) => !term || p.label.toLowerCase().includes(term))
      .filter((p) => !hideSettled || Math.abs(p.ending) >= 0.005)
      .sort((a, b) => (!a[cfg.id]) - (!b[cfg.id]) || a.label.localeCompare(b.label))
  }, [data, search, hideSettled, cfg])

  const unlinked = (data?.parties || []).find((p) => !p[cfg.id])
  const code = data?.meta?.codes?.join(', ')
  const sum = (key) => parties.reduce((s, p) => s + (Number(p[key]) || 0), 0)
  const agingRows = useMemo(() => {
    const term = search.trim().toLowerCase()
    return (aging?.[cfg.aging.rows] || []).filter((r) => !term || String(r[cfg.name] || '').toLowerCase().includes(term))
  }, [aging, search, cfg])
  // The aging should add up to the subledger; both are checked over everyone, not just the rows shown.
  const agingTotal = (aging?.[cfg.aging.rows] || []).reduce((s, r) => s + (Number(r.balance) || 0), 0)
  const ledgerTotal = (data?.parties || []).reduce((s, p) => s + (Number(p.ending) || 0), 0)
  const agingDiff = Math.round((agingTotal - ledgerTotal) * 100) / 100

  const exportCsv = () => downloadCsv(cfg.file, [
    [cfg.party, 'Date', 'Entry', 'Source', 'Description', cfg.upLabel, cfg.downLabel, 'Balance'],
    ...parties.flatMap((p) => [
      [p.label, start, '', '', 'Opening balance', '', '', p.opening.toFixed(2)],
      ...(p.lines || []).map((l) => ['', l.date, docNo('je', l.je_no), l.source?.label, l.memo,
        l[cfg.up] ? l[cfg.up].toFixed(2) : '', l[cfg.down] ? l[cfg.down].toFixed(2) : '', l.running.toFixed(2)]),
      ['', end, '', '', 'Closing balance', p[cfg.up].toFixed(2), p[cfg.down].toFixed(2), p.ending.toFixed(2)],
    ]),
    [],
    [cfg.aging.title, `As of ${end}`],
    [cfg.party, 'Balance', 'Not yet due', '1–30 days', '31–60 days', '61–90 days', 'Over 90 days', 'Oldest (days)'],
    ...agingRows.map((r) => [r[cfg.name] || '', r.balance.toFixed(2), r.current.toFixed(2), r.d30.toFixed(2), r.d60.toFixed(2), r.d90.toFixed(2), r.d90p.toFixed(2), r.days_outstanding]),
  ])

  return (
    <div className="ci-page fs-page">
      <ReportsTabs />
      <div className="ci-header-row fs-noprint">
        <div className="ci-title-block">
          <div>
            <h1>{cfg.title} <HelpTip k={cfg.k} /></h1>
            <div className="ci-title-meta">{cfg.meta}</div>
          </div>
        </div>
        <div className="ci-header-actions">
          <button type="button" className="ci-btn ci-btn-ghost" disabled={!data} onClick={exportCsv}><Download size={15} />Export</button>
          <button type="button" className="ci-btn ci-btn-ghost" disabled={!data} onClick={() => printPage()}><Printer size={15} />Print</button>
        </div>
      </div>

      <div className="ci-toolbar fs-noprint">
        <PeriodPicker period={period} start={start} end={end} setPeriod={setPeriod} set={set} />
        <label className="ci-search" aria-label={`Search ${cfg.party.toLowerCase()}s`}>
          <Search size={14} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`${cfg.party} name…`} />
        </label>
        <label className="ci-form-toggle fs-inline">
          <input type="checkbox" checked={hideSettled} onChange={(e) => setHideSettled(e.target.checked)} />
          Only with a balance
        </label>
      </div>

      {error && <ErrorBanner message={error} />}

      {!data ? (!error && <Spinner label={`Preparing ${cfg.title.toLowerCase()}…`} />) : (
        <>
        <section className="fs-card">
          <StatementHead company={company} title={cfg.full} sub={`For the period ${periodLabel(data.meta.start, data.meta.end)}`} />
          <div className="fs-check fs-noprint">
            {!unlinked || Math.abs(unlinked.ending) < 0.005
              ? <StatusBadge label={`Agrees with account ${code} in the general ledger`} tone="good" />
              : <StatusBadge label={`${money(Math.abs(unlinked.ending))} in account ${code} isn’t linked to a ${cfg.party.toLowerCase()}`} tone="warn" />}
            <HelpTip k="subledgerTie" />
          </div>
          <table className="fs-table gl-table">
            <thead>
              <tr>
                <th className="gl-date hide-sm">Date</th>
                <th className="gl-entry hide-sm">Entry</th>
                <th>{cfg.party}</th>
                <th className="num hide-sm">{cfg.upLabel}</th>
                <th className="num hide-sm">{cfg.downLabel}</th>
                <th className="num">Balance</th>
              </tr>
            </thead>
            <tbody>
              {parties.length === 0 && (
                <tr className="fs-note"><td colSpan="6">{search.trim() || hideSettled ? `No ${cfg.party.toLowerCase()}s match.` : `No ${cfg.title.toLowerCase()} in this period.`}</td></tr>
              )}
              {parties.map((p) => {
                const shown = !!open[p.key]
                return (
                  <Fragment key={p.key}>
                    <tr className={`gl-account-row${p[cfg.id] ? '' : ' sl-unlinked'}`}>
                      <td colSpan="3">
                        <button type="button" className="fs-toggle gl-toggle" aria-expanded={shown} onClick={() => setOpen((o) => ({ ...o, [p.key]: !shown }))}>
                          {shown ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
                          {p.label}
                        </button>
                        {!p[cfg.id] && <HelpTip k="notLinked" />}
                        {Math.abs(p.opening) >= 0.005 && <span className="fs-extra">Opening {acct(p.opening)}</span>}
                      </td>
                      <td className="num hide-sm">{amount(p[cfg.up])}</td>
                      <td className="num hide-sm">{amount(p[cfg.down])}</td>
                      <td className="num">{acct(p.ending)}</td>
                    </tr>
                    {shown && (
                      <>
                        <tr className="fs-detail gl-edge">
                          <td className="gl-date hide-sm">{fmtDay(data.meta.start)}</td>
                          <td className="hide-sm" />
                          <td>Opening balance</td>
                          <td className="num hide-sm" /><td className="num hide-sm" />
                          <td className="num">{acct(p.opening)}</td>
                        </tr>
                        {(p.lines || []).map((l, i) => (
                          <tr key={`${l.je_id}-${i}`} className="fs-detail">
                            <td className="gl-date hide-sm">{fmtDay(l.date)}</td>
                            <td className="hide-sm"><Link className="fs-link" to={`/accounting/journal/${l.je_id}`}>{docNo('je', l.je_no) || l.je_id}</Link></td>
                            <td>
                              {l.memo || '—'}
                              <span className="cell-sub">
                                {l.source?.link ? <Link className="fs-link" to={l.source.link}>{l.source.label}</Link> : l.source?.label}
                                <span className="show-sm">
                                  {' · '}{fmtDay(l.date)}{l[cfg.up] ? ` · ${cfg.upLabel} ${money(l[cfg.up])}` : ''}{l[cfg.down] ? ` · ${cfg.downLabel} ${money(l[cfg.down])}` : ''}
                                  {' · '}<Link className="fs-link" to={`/accounting/journal/${l.je_id}`}>{docNo('je', l.je_no) || l.je_id}</Link>
                                </span>
                              </span>
                            </td>
                            <td className="num hide-sm">{amount(l[cfg.up])}</td>
                            <td className="num hide-sm">{amount(l[cfg.down])}</td>
                            <td className="num">{acct(l.running)}</td>
                          </tr>
                        ))}
                        {p[cfg.id] && cfg.link && (
                          <tr className="fs-detail gl-edge fs-noprint">
                            <td className="gl-date hide-sm" /><td className="hide-sm" />
                            <td colSpan="4"><Link className="fs-link" to={cfg.link(p[cfg.id])}>Open {p.label}’s account</Link></td>
                          </tr>
                        )}
                      </>
                    )}
                  </Fragment>
                )
              })}
              {parties.length > 0 && (
                <tr className="fs-grand">
                  <td colSpan="3">Total{code ? <Link className="fs-link fs-extra fs-noprint" to={glHref(data.meta.codes[0], data.meta.start, data.meta.end)}>Account {code} in the general ledger</Link> : null}</td>
                  <td className="num hide-sm">{money(sum(cfg.up))}</td>
                  <td className="num hide-sm">{money(sum(cfg.down))}</td>
                  <td className="num">{acct(sum('ending'))}</td>
                </tr>
              )}
            </tbody>
          </table>
        </section>

        <section className="fs-card sl-aging-card">
          <StatementHead company={company} title={cfg.aging.title} sub={`As of ${fmtDay(data.meta.end)}`} />
          <div className="fs-check fs-noprint">
            {!aging ? <span className="sum-muted">Working out the aging…</span> : Math.abs(agingDiff) < 0.005
              ? <StatusBadge label="Agrees with the subledger" tone="good" />
              : <StatusBadge label={`${money(Math.abs(agingDiff))} ${agingDiff > 0 ? 'more' : 'less'} than the subledger`} tone="warn" />}
            <HelpTip text={`Open ${cfg.aging.what} by how long past their due date they are on the last day of the period. Not yet due means the due date hasn’t passed. The subledger comes from the books and the aging from the ${cfg.aging.what} themselves, so they differ when ${cfg.aging.what} were recorded without an entry, an entry has no ${cfg.aging.one}, or an opening balance was entered as one amount.`} />
          </div>
          {aging && <AgingTable party={cfg.party} rows={agingRows} idKey={cfg.id} nameKey={cfg.name} link={cfg.aging.link} emptyText={search.trim() ? `No ${cfg.party.toLowerCase()}s match.` : cfg.aging.empty} />}
        </section>
        </>
      )}
    </div>
  )
}
